import React, { useState } from 'react';
import { FaTimes, FaPaperPlane, FaCheckCircle, FaLock, FaExclamationCircle } from 'react-icons/fa';
import { supabase } from '../services/supabaseClient';

interface EmailCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  dataToSend: any;
  regionName?: string;
  selectionTitle?: string;
  selectionType?: 'activities' | 'services' | 'custom';
  searchQuery?: string;
}

export default function EmailCaptureModal({
  isOpen,
  onClose,
  dataToSend,
  regionName,
  selectionTitle,
  selectionType = 'custom',
  searchQuery,
}: EmailCaptureModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const normalizeItems = (data: any) => {
    if (!data) return [];
    if (!Array.isArray(data)) return [data];
    return data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!consent) {
      setErrorMessage('Veuillez accepter la politique de confidentialité.');
      setStatus('error');
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setErrorMessage('Veuillez entrer une adresse courriel valide.');
      setStatus('error');
      return;
    }

    setStatus('sending');

    try {
      const { data, error } = await supabase.functions.invoke('send-region-selection-email', {
        body: {
          name: name.trim(),
          email: cleanEmail,
          consent,
          regionName: regionName || 'Québec',
          selectionTitle: selectionTitle || 'Sélection Forfeo',
          selectionType,
          searchQuery: searchQuery || '',
          items: normalizeItems(dataToSend),
          sourceUrl: typeof window !== 'undefined' ? window.location.href : '',
        },
      });

      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.error || data.message || 'Envoi impossible.');

      setStatus('success');
      setTimeout(() => {
        onClose();
        setStatus('idle');
        setEmail('');
        setName('');
        setConsent(false);
      }, 3000);
    } catch (err: any) {
      console.error('Échec envoi sélection région:', err);
      setErrorMessage(err?.message || "L'envoi a échoué. Veuillez réessayer.");
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden relative animate-fade-in-up">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition z-10" aria-label="Fermer">
          <FaTimes size={20}/>
        </button>

        {status === 'success' && (
          <div className="p-10 text-center">
            <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
              <FaCheckCircle />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">C'est envoyé !</h3>
            <p className="text-slate-500">Surveillez votre boîte de réception et vos indésirables.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="p-10 text-center">
            <div className="w-20 h-20 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
              <FaExclamationCircle />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">Oups, une erreur...</h3>
            <p className="text-slate-500 mb-6">{errorMessage || "L'envoi a échoué. Vérifiez votre connexion ou réessayez."}</p>
            <button onClick={() => setStatus('idle')} className="text-blue-600 font-bold hover:underline">
              Retourner au formulaire
            </button>
          </div>
        )}

        {(status === 'idle' || status === 'sending') && (
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="inline-block p-3 bg-blue-100 text-blue-600 rounded-full mb-4 text-xl">
                <FaPaperPlane />
              </div>
              <h3 className="text-2xl font-bold text-slate-800">Sauvegarder ma sélection</h3>
              <p className="text-slate-500 text-sm mt-2">Recevez cette liste de recommandations directement par courriel.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Prénom</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Votre prénom"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Courriel</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="exemple@email.com"
                />
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                <input
                  type="checkbox"
                  required
                  id="consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="consent" className="text-xs text-slate-500 cursor-pointer leading-relaxed">
                  J'accepte de recevoir cette sélection et des offres exclusives de Forfeo. (Loi 25).
                </label>
              </div>

              <button
                type="submit"
                disabled={!consent || status === 'sending'}
                className="w-full bg-blue-600 text-white font-bold py-4 rounded-xl hover:bg-blue-700 transition shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {status === 'sending' ? 'Envoi en cours...' : 'Envoyer ma liste maintenant'}
              </button>

              <div className="text-center flex items-center justify-center gap-1 text-[10px] text-slate-400 mt-4">
                <FaLock size={10}/> Informations sécurisées. Pas de spam.
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
